import React from 'react';
import PropTypes from 'prop-types';
import { alpha, Box, CircularProgress, Stack, Typography } from '@mui/material';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { C, FONT_NUM } from './tokens';
import { fmtNum, signalStyle, triggerStyle } from './formatters';
import { Tag } from './atoms';
import { signalHistoryPointShape } from './shapes';

// Tooltip body for the breakout signal history area chart.
const SignalHistoryTooltipContent = ({ active, payload, label, t, getMetadataLabel }) => {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload;
  if (!point) return null;
  return (
    <Box sx={{
      bgcolor: '#fff',
      border: `1px solid ${C.border}`,
      borderRadius: 1.25,
      px: 1,
      py: 0.8,
      boxShadow: '0 8px 24px rgba(15, 23, 42, 0.10)',
    }}>
      <Typography sx={{ fontSize: '0.66rem', color: C.muted, fontWeight: 700 }}>
        {point.score_date || label}
      </Typography>
      <Typography sx={{ fontSize: '0.82rem', color: C.ink, fontWeight: 800, fontFamily: FONT_NUM, mt: 0.2 }}>
        {fmtNum(point.breakout_score, 1)}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.45, mt: 0.5, flexWrap: 'wrap' }}>
        <Tag {...signalStyle(point.signal)} sx={{ height: 18, px: 0.6 }}>
          {getMetadataLabel('signals', point.signal, 'short_label')}
        </Tag>
        <Tag
          bg={triggerStyle(point.trigger_type).bg}
          fg={triggerStyle(point.trigger_type).fg}
          sx={{ height: 18, px: 0.6 }}
        >
          {t(
            `breakoutCandidates.enums.triggers.${(point.trigger_type || 'NONE').toUpperCase()}`,
            point.trigger_type || '—',
          )}
        </Tag>
      </Box>
    </Box>
  );
};

SignalHistoryTooltipContent.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array, // eslint-disable-line react/forbid-prop-types
  label: PropTypes.string,
  t: PropTypes.func.isRequired,
  getMetadataLabel: PropTypes.func.isRequired,
};

SignalHistoryTooltipContent.defaultProps = {
  active: false,
  payload: [],
  label: '',
};

// Area chart of historical breakout scores for the selected ticker.
const SignalHistoryPanel = ({ detailHistory, detailHistoryLoading, t, getMetadataLabel }) => {
  if (detailHistoryLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, py: 1 }}>
        <CircularProgress size={16} />
        <Typography sx={{ fontSize: 12, color: C.muted }}>
          {t('breakoutCandidates.drawer.signalHistoryLoading')}
        </Typography>
      </Box>
    );
  }
  if (detailHistory?.error) {
    return (
      <Typography sx={{ fontSize: 12, color: C.red, py: 0.8 }}>
        {detailHistory.error}
      </Typography>
    );
  }
  const history = Array.isArray(detailHistory?.history) ? detailHistory.history : [];
  if (history.length === 0) {
    return (
      <Typography sx={{ fontSize: 12, color: C.mutedSoft, py: 0.8 }}>
        {t('breakoutCandidates.drawer.signalHistoryEmpty')}
      </Typography>
    );
  }
  const latest = history[history.length - 1];
  const chartData = history.map((item) => ({
    ...item,
    date_short: item.score_date ? item.score_date.slice(5) : '—',
  }));
  const renderTooltip = (props) => (
    <SignalHistoryTooltipContent {...props} t={t} getMetadataLabel={getMetadataLabel} />
  );
  return (
    <>
      <Box sx={{ height: 190 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="breakoutSignalHistoryFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={alpha(C.accent, 0.28)} />
                <stop offset="100%" stopColor={alpha(C.accent, 0.02)} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={alpha(C.border, 0.9)} />
            <XAxis
              dataKey="date_short"
              tick={{ fontSize: 11, fill: C.muted }}
              tickLine={false}
              axisLine={false}
              minTickGap={18}
            />
            <YAxis
              domain={['dataMin - 3', 'dataMax + 3']}
              tick={{ fontSize: 11, fill: C.muted }}
              tickLine={false}
              axisLine={false}
              width={34}
            />
            <RechartsTooltip content={renderTooltip} />
            <Area
              type="monotone"
              dataKey="breakout_score"
              stroke={C.accent}
              strokeWidth={2.4}
              fill="url(#breakoutSignalHistoryFill)"
              activeDot={{ r: 4, strokeWidth: 0, fill: C.accent }}
              dot={{ r: 2.5, strokeWidth: 0, fill: alpha(C.accent, 0.9) }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: '0.72rem', color: C.muted }}>
          {t('breakoutCandidates.drawer.signalHistoryLatest')}
        </Typography>
        <Stack direction="row" spacing={0.6} useFlexGap flexWrap="wrap">
          <Tag {...signalStyle(latest?.signal)}>
            {getMetadataLabel('signals', latest?.signal, 'short_label')}
          </Tag>
          <Tag
            bg={triggerStyle(latest?.trigger_type).bg}
            fg={triggerStyle(latest?.trigger_type).fg}
          >
            {t(
              `breakoutCandidates.enums.triggers.${(latest?.trigger_type || 'NONE').toUpperCase()}`,
              latest?.trigger_type || '—',
            )}
          </Tag>
        </Stack>
      </Box>
    </>
  );
};

SignalHistoryPanel.propTypes = {
  detailHistory: PropTypes.shape({
    history: PropTypes.arrayOf(signalHistoryPointShape),
    error: PropTypes.string,
  }),
  detailHistoryLoading: PropTypes.bool,
  t: PropTypes.func.isRequired,
  getMetadataLabel: PropTypes.func.isRequired,
};

SignalHistoryPanel.defaultProps = {
  detailHistory: null,
  detailHistoryLoading: false,
};

export default SignalHistoryPanel;
